import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { contactsAPI } from '../services/api';
import { useScrollPosition } from '../hooks/useScrollPosition';

const ContactsContext = createContext();
const ContactsSummaryContext = createContext();

export const useContacts = () => {
  const context = useContext(ContactsContext);
  if (!context) {
    throw new Error('useContacts must be used within a ContactsProvider');
  }
  return context;
};

export const useContactsSummary = () => {
  const context = useContext(ContactsSummaryContext);
  if (!context) {
    throw new Error('useContactsSummary must be used within a ContactsProvider');
  }
  return context;
};

export const ContactsProvider = ({ children }) => {
  const [contacts, setContacts] = useState([]);
  const [contactsSummary, setContactsSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Хук для сохранения позиции скролла
  const { executeWithScrollPreservation } = useScrollPosition();

  // Вспомогательная функция для обработки ошибок
  const handleError = useCallback((error, operation) => {
    console.error(`Error ${operation}:`, error);
    throw error;
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contactsAPI.getContacts();
      setContacts(response.data);
    } catch (error) {
      handleError(error, 'loading contacts');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const loadContactsSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const response = await contactsAPI.getContactsSummary();
      setContactsSummary(response.data);
    } catch (error) {
      handleError(error, 'loading contacts summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [handleError]);

  const addContact = async (contactData) => {
    try {
      const response = await contactsAPI.addContact(contactData);
      setContacts(prev => [...prev, response.data]);
      // Также обновляем summary
      setContactsSummary(prev => [...prev, {
        _id: response.data._id,
        name: response.data.name,
        tags: response.data.tags,
        structuredCountry: response.data.structuredCountry,
        structuredCity: response.data.structuredCity,
        coordinates: response.data.coordinates
      }]);
      return response.data;
    } catch (error) {
      handleError(error, 'adding contact');
    }
  };

  const addEncounter = async (encounterData) => {
    try {
      const response = await contactsAPI.addEncounter(encounterData);
      setContacts(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setContactsSummary(prev => [...prev, {
        _id: response.data._id,
        name: response.data.name,
        tags: response.data.tags,
        structuredCountry: response.data.structuredCountry,
        structuredCity: response.data.structuredCity,
        coordinates: response.data.coordinates
      }].sort((a, b) => a.name.localeCompare(b.name)));
      return response.data;
    } catch (error) {
      handleError(error, 'adding encounter');
    }
  };

  const updateContact = async (contactId, contactData) => {
    return executeWithScrollPreservation(async () => {
      try {
        const response = await contactsAPI.editContact(contactId, contactData);
        const updatedContact = response.data;

        setContacts(prev => prev.map(contact =>
          contact._id === contactId ? updatedContact : contact
        ));

        // Также обновляем summary с правильными полями
        setContactsSummary(prev => prev.map(contact =>
          contact._id === contactId ? {
            _id: updatedContact._id,
            name: updatedContact.name,
            tags: updatedContact.tags,
            // Добавляем другие поля которые могут понадобиться для summary
            structuredCountry: updatedContact.structuredCountry,
            structuredCity: updatedContact.structuredCity,
            coordinates: updatedContact.coordinates, // Добавляем координаты
            lastInteraction: updatedContact.lastInteraction
          } : contact
        ));

        return updatedContact;
      } catch (error) {
        handleError(error, 'updating contact');
      }
    });
  };

  const deleteContact = async (contactId) => {
    return executeWithScrollPreservation(async () => {
      try {
        await contactsAPI.deleteContact(contactId);
        setContacts(prev => prev.filter(contact => contact._id !== contactId));
        // Также обновляем summary
        setContactsSummary(prev => prev.filter(contact => contact._id !== contactId));
      } catch (error) {
        handleError(error, 'deleting contact');
      }
    });
  };

  const archiveContact = async (contactId) => {
    return executeWithScrollPreservation(async () => {
      try {
        const response = await contactsAPI.archiveContact(contactId);
        const updatedContact = response.data;

        // Удаляем контакт из текущих списков (так как он теперь архивирован)
        setContacts(prev => prev.filter(contact => contact._id !== contactId));
        setContactsSummary(prev => prev.filter(contact => contact._id !== contactId));

        return updatedContact;
      } catch (error) {
        handleError(error, 'archiving contact');
      }
    });
  };

  const unarchiveContact = async (contactId) => {
    return executeWithScrollPreservation(async () => {
      try {
        const response = await contactsAPI.unarchiveContact(contactId);
        const updatedContact = response.data;

        // Добавляем контакт обратно в списки
        setContacts(prev => [...prev, updatedContact].sort((a, b) => a.name.localeCompare(b.name)));
        setContactsSummary(prev => [...prev, {
          _id: updatedContact._id,
          name: updatedContact.name,
          tags: updatedContact.tags,
          archived: updatedContact.archived,
          structuredCountry: updatedContact.structuredCountry,
          structuredCity: updatedContact.structuredCity,
          coordinates: updatedContact.coordinates
        }].sort((a, b) => a.name.localeCompare(b.name)));

        return updatedContact;
      } catch (error) {
        handleError(error, 'unarchiving contact');
      }
    });
  };

  const addInteraction = async (contactId, interactionData) => {
    return executeWithScrollPreservation(async () => {
      try {
        const response = await contactsAPI.addInteraction(contactId, interactionData);
        const updatedContact = response.data;

        // Обновляем полный контакт в state, включая новые interactions
        setContacts(prev => prev.map(contact =>
          contact._id === contactId ? updatedContact : contact
        ));

        // Возвращаем новое interaction (последнее в массиве)
        const newInteraction = updatedContact.interactions[0]; // Новое interaction всегда первое после сортировки
        return newInteraction;
      } catch (error) {
        handleError(error, 'adding interaction');
      }
    });
  };

  const deleteInteraction = async (contactId, interactionId) => {
    return executeWithScrollPreservation(async () => {
      try {
        const response = await contactsAPI.deleteInteraction(contactId, interactionId);
        const updatedContact = response.data;

        // Обновляем полный контакт в state
        setContacts(prev => prev.map(contact =>
          contact._id === contactId ? updatedContact : contact
        ));
      } catch (error) {
        handleError(error, 'deleting interaction');
      }
    });
  };

  const updateInteraction = async (contactId, interactionId, interactionData) => {
    return executeWithScrollPreservation(async () => {
      try {
        const response = await contactsAPI.updateInteraction(contactId, interactionId, interactionData);
        const updatedContact = response.data;

        // Обновляем полный контакт в state
        setContacts(prev => prev.map(contact =>
          contact._id === contactId ? updatedContact : contact
        ));

        // Возвращаем обновленное interaction
        const updatedInteraction = updatedContact.interactions.find(
          interaction => interaction._id === interactionId
        );
        return updatedInteraction;
      } catch (error) {
        handleError(error, 'updating interaction');
      }
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadContacts();
      loadContactsSummary(); // Загружаем и краткую информацию
    } else {
      setLoading(false);
      setSummaryLoading(false);
    }
  }, [loadContacts, loadContactsSummary]);

  // Мемоизация value для предотвращения лишних ре-рендеров
  const value = useMemo(() => ({
    contacts,
    setContacts,
    loading,
    loadContacts,
    addContact,
    addEncounter,
    updateContact,
    deleteContact,
    archiveContact,
    unarchiveContact,
    addInteraction,
    deleteInteraction,
    updateInteraction
  }), [
    contacts,
    loading,
    loadContacts,
    addContact,
    addEncounter,
    updateContact,
    deleteContact,
    archiveContact,
    unarchiveContact,
    addInteraction,
    deleteInteraction,
    updateInteraction
  ]);

  const summaryValue = useMemo(() => ({
    contactsSummary,
    setContactsSummary,
    summaryLoading,
    loadContactsSummary
  }), [contactsSummary, summaryLoading, loadContactsSummary]);

  // Слушаем событие успешной авторизации
  useEffect(() => {
    const handleAuthSuccess = () => {
      loadContacts();
      loadContactsSummary(); // Также перезагружаем summary
    };

    document.addEventListener('auth-success', handleAuthSuccess);
    return () => {
      document.removeEventListener('auth-success', handleAuthSuccess);
    };
  }, [loadContacts, loadContactsSummary]);

  return (
    <ContactsContext.Provider value={value}>
      <ContactsSummaryContext.Provider value={summaryValue}>
        {children}
      </ContactsSummaryContext.Provider>
    </ContactsContext.Provider>
  );
}; 
